import React from "react";
import {Box, Typography} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import {Calendar, Truck} from "lucide-react";
import {ROUTE_PROGRESS_BY_STATUS} from "./trackingConfig";

const RouteEndpoint = ({label, value, align = "left"}) => (
    <Box sx={{minWidth: 0, textAlign: align}}>
        <Typography variant="caption" sx={{display: "block", mb: 0.25, color: "neutral.500", fontWeight: 700, letterSpacing: "0.04em"}}>
            {label}
        </Typography>
        <Typography variant="subtitle1" sx={{fontSize: {xs: 14, sm: 16}, lineHeight: 1.35, overflowWrap: "anywhere"}}>
            {value}
        </Typography>
    </Box>
);

const RouteProgress = ({progress}) => {
    const theme = useTheme();
    return (
        <Box aria-label={`Shipment route ${progress}% complete`} sx={{position: "relative", height: 28, display: "flex", alignItems: "center"}}>
            <Box sx={{position: "absolute", left: 0, right: 0, height: 2, borderRadius: 999, backgroundColor: "neutral.200"}} />
            <Box sx={{position: "absolute", left: 0, width: `${progress}%`, height: 2, borderRadius: 999, backgroundColor: "primary.main"}} />
            <Box sx={{position: "absolute", left: 0, width: 8, height: 8, borderRadius: "50%", backgroundColor: "primary.main"}} />
            <Box sx={{position: "absolute", left: `${progress}%`, transform: "translateX(-50%)", width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "background.paper", border: 2, borderColor: "primary.main"}}>
                <Truck size={13} color={theme.palette.primary.main} aria-hidden="true" />
            </Box>
            <Box sx={{position: "absolute", right: 0, width: 10, height: 10, borderRadius: "50%", backgroundColor: "background.paper", border: 2, borderColor: progress >= 100 ? "primary.main" : "neutral.300"}} />
        </Box>
    );
};

export const TrackingShipmentRoute = ({result}) => {
    const theme = useTheme();
    const progress = ROUTE_PROGRESS_BY_STATUS[result.status] ?? 5;
    return (
        <>
            <Box sx={{backgroundColor: "primary.main", color: "primary.contrastText", px: {xs: 1.5, sm: 2, md: 2.5}, py: {xs: 1.25, md: 1.75}, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1}}>
                <Box sx={{display: "inline-flex", alignItems: "center", gap: 0.75, backgroundColor: alpha(theme.palette.background.paper, 0.16), borderRadius: 999, pl: 1, pr: 1.25, py: 0.5}}>
                    <Box sx={{width: 8, height: 8, borderRadius: "50%", backgroundColor: "background.paper"}} />
                    <Typography variant="statusPill">{result.status}</Typography>
                </Box>
                {result.status !== "Delivered" ? (
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                        <Calendar size={15} color={alpha(theme.palette.background.paper, 0.85)} aria-hidden="true" />
                        <Typography variant="caption" sx={{color: "inherit", opacity: 0.85, fontWeight: 700}}>ETA</Typography>
                        <Typography variant="subtitle2" sx={{color: "inherit"}}>{result.eta}</Typography>
                    </Box>
                ) : <Typography variant="subtitle2" sx={{color: "inherit"}}>Delivered</Typography>}
            </Box>
            <Box sx={{backgroundColor: "background.paper", px: {xs: 1.5, sm: 2, md: 2.5}, pt: {xs: 1.5, md: 2.25}}}>
                <Box sx={{display: {xs: "grid", sm: "none"}, gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", rowGap: 0.75}}>
                    <RouteEndpoint label="FROM" value={result.origin} />
                    <RouteEndpoint label="TO" value={result.destination} align="right" />
                    <Box sx={{gridColumn: "1 / -1", px: 0.25}}>
                        <RouteProgress progress={progress} />
                    </Box>
                </Box>
                <Box sx={{display: {xs: "none", sm: "grid"}, gridTemplateColumns: "auto minmax(100px, 1fr) auto", alignItems: "center", gap: 2}}>
                    <RouteEndpoint label="FROM" value={result.origin} />
                    <RouteProgress progress={progress} />
                    <RouteEndpoint label="TO" value={result.destination} align="right" />
                </Box>
            </Box>
        </>
    );
};
